import { Badge, Box, Heading, HStack, SimpleGrid, Text, VStack } from "@chakra-ui/react"
import { EstimatedEpcAfterUpgrades, Home } from "../../services/portfolioApi"
import { formatOverviewDate } from "../../utils/overviewUtils"
import EpcRatingChart from "./EpcRatingChart"

interface OverviewTabProps {
  home: Home
  bedrooms?: number | null
}

const InsulationBadge = ({ insulated }: { insulated: string | undefined | null }) => {
  if (!insulated) return null
  const isInsulated = insulated === "Yes"
  return (
    <Badge
      colorPalette={isInsulated ? "green" : "orange"}
      variant="subtle"
      fontSize="2xs"
      px={1.5}
      py={0.5}
      borderRadius="sm"
    >
      {isInsulated ? "Insulated" : "Not insulated"}
    </Badge>
  )
}

const SpecRow = ({
  label,
  value,
  badge
}: {
  label: string
  value?: string | null
  badge?: React.ReactNode
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    py={3}
    borderBottom="1px solid"
    borderColor="gray.100"
    gap={3}
    _last={{ borderBottom: "none" }}
  >
    <Text fontSize="sm" color="gray.500" flexShrink={0} minW="90px">
      {label}
    </Text>
    <Box display="flex" alignItems="center" gap={2} justifyContent="flex-end" textAlign="right">
      {value && (
        <Text fontSize="sm" fontWeight="medium" color="gray.800">
          {value}
        </Text>
      )}
      {badge}
    </Box>
  </Box>
)

const PropertyFact = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) return null
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="medium" color="gray.800">
        {value}
      </Text>
    </Box>
  )
}

const SectionCard = ({
  title,
  children
}: {
  title: string
  children: React.ReactNode
}) => (
  <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
    <Box px={5} py={4} borderBottom="1px solid" borderColor="gray.100" bg="gray.50">
      <Heading size="sm" color="gray.800">
        {title}
      </Heading>
    </Box>
    <Box px={5} py={2}>
      {children}
    </Box>
  </Box>
)

const formatBandScore = (band?: string | null, score?: number | null) => {
  if (score == null) return band || "-"
  return band ? `${band}${Math.round(score)}` : String(Math.round(score))
}

const formatRounded = (value?: number | null, suffix = "") => {
  if (value == null) return null
  return `${Math.round(value).toLocaleString()}${suffix}`
}

const confidenceColor = (confidence: EstimatedEpcAfterUpgrades["confidence"]) => {
  if (confidence === "high") return "green"
  if (confidence === "medium") return "yellow"
  return "orange"
}

const EpcUpgradeEstimate = ({
  estimate
}: {
  estimate?: EstimatedEpcAfterUpgrades | null
}) => {
  if (!estimate) return null

  const inputFacts = [
    {
      label: "Solar",
      value: estimate.inputs.solarPanels
        ? `${estimate.inputs.solarPanels} panels${estimate.inputs.solarKw ? `, ${estimate.inputs.solarKw.toFixed(1)} kWp` : ""}`
        : null
    },
    {
      label: "Generation",
      value: formatRounded(estimate.inputs.solarGenerationKwh, " kWh/yr")
    },
    {
      label: "Battery",
      value: estimate.inputs.batteryKwh ? `${estimate.inputs.batteryKwh.toFixed(1)} kWh` : null
    },
    {
      label: "Heat pump",
      value: estimate.inputs.heatPumpKw ? `${estimate.inputs.heatPumpKw.toFixed(1)} kW` : null
    }
  ].filter((fact) => fact.value)

  const rangeText = `${formatBandScore(
    estimate.bandRange.low,
    estimate.scoreRange.low
  )}-${formatBandScore(estimate.bandRange.high, estimate.scoreRange.high)}`

  return (
    <Box mt={5} pt={4} borderTop="1px solid" borderColor="gray.100">
      <HStack justify="space-between" align="start" gap={4} flexWrap="wrap">
        <Box>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold">
            After GreenFlip upgrades
          </Text>
          <HStack gap={3} align="baseline" mt={1}>
            <Heading size="lg" color="gray.900">
              {formatBandScore(estimate.estimatedBand, estimate.estimatedScore)}
            </Heading>
            <Text fontSize="sm" color="gray.600">
              likely range {rangeText}
            </Text>
          </HStack>
        </Box>
        <Badge
          colorPalette={confidenceColor(estimate.confidence)}
          variant="subtle"
          textTransform="capitalize"
          flexShrink={0}
        >
          {estimate.confidence} confidence
        </Badge>
      </HStack>

      {inputFacts.length > 0 && (
        <SimpleGrid columns={{ base: 2, sm: 4 }} gap={3} mt={4}>
          {inputFacts.map((fact) => (
            <Box key={fact.label}>
              <Text fontSize="xs" color="gray.500">
                {fact.label}
              </Text>
              <Text fontSize="sm" fontWeight="medium" color="gray.800">
                {fact.value}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      )}
    </Box>
  )
}

const OverviewTab = ({ home, bedrooms }: OverviewTabProps) => {
  const formatDate = formatOverviewDate

  const keyFacts = [
    {
      label: "Property type",
      value: [home.features.propertyType, home.features.builtForm]
        .filter(Boolean)
        .join(", ") || null
    },
    { label: "Year built", value: home.features.constructionYear ? String(home.features.constructionYear) : null },
    { label: "Floor area", value: home.features.floorArea ? `${home.features.floorArea} m²` : null },
    { label: "Storeys", value: home.features.storeys ? String(home.features.storeys) : null },
    {
      label: "Rooms",
      value: [
        bedrooms != null ? `${bedrooms} bed` : null,
        home.features.numberOfHabitableRooms ? `${home.features.numberOfHabitableRooms} habitable` : null,
        home.features.numberOfHeatedRooms ? `${home.features.numberOfHeatedRooms} heated` : null
      ].filter(Boolean).join(" · ") || null
    }
  ].filter(f => f.value)

  return (
    <VStack gap={5} align="stretch">
      {/* Property overview */}
      {keyFacts.length > 0 && (
        <SectionCard title="Property Overview">
          <Box py={3}>
            <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} gap={5}>
              {keyFacts.map(fact => (
                <PropertyFact key={fact.label} label={fact.label} value={fact.value} />
              ))}
            </SimpleGrid>
          </Box>
        </SectionCard>
      )}

      {/* EPC + spec – side by side */}
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} alignItems="start">
        {/* Left: EPC */}
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
          <Box px={5} py={4} borderBottom="1px solid" borderColor="gray.100" bg="gray.50" display="flex" alignItems="center" justifyContent="space-between">
            <Heading size="sm" color="gray.800">
              Energy Performance Certificate
            </Heading>
            {home.features.currentEpcRating && (
              <Text fontSize="xs" color="gray.500">
                {formatDate(home.features.epcInspectionDate)}
              </Text>
            )}
          </Box>
          <Box px={5} py={5}>
            {home.features.currentEpcRating ? (
              <EpcRatingChart
                currentRating={home.features.currentEpcRating}
                currentScore={home.features.currentEpcEfficiency}
                potentialRating={home.features.potentialRating}
                potentialScore={home.features.potentialEpcEfficiency}
              />
            ) : (
              <Box
                bg="orange.50"
                border="1px solid"
                borderColor="orange.200"
                borderRadius="md"
                p={4}
              >
                <Text fontSize="sm" color="orange.800">
                  No EPC certificate could be found for this property. The condition of this
                  home has been estimated based on all available information for this location
                  and property type.
                </Text>
              </Box>
            )}
            <EpcUpgradeEstimate estimate={home.estimatedEpcAfterUpgrades} />
          </Box>
        </Box>

        {/* Right: Building Fabric + Heating stacked */}
        <VStack gap={4} align="stretch">
          <SectionCard title="Building Fabric">
            <SpecRow
              label="Walls"
              value={home.features.wallType}
              badge={<InsulationBadge insulated={home.features.wallInsulation} />}
            />
            <SpecRow
              label="Roof"
              value={[
                home.features.roofType,
                home.features.roofInsulationThickness
                  ? `${home.features.roofInsulationThickness}mm insulation`
                  : null
              ].filter(Boolean).join(" · ")}
              badge={<InsulationBadge insulated={home.features.roofInsulated} />}
            />
            <SpecRow
              label="Floor"
              value={home.features.floorType}
              badge={<InsulationBadge insulated={home.features.floorInsulated} />}
            />
            <SpecRow
              label="Windows"
              value={home.features.windowsType}
            />
          </SectionCard>

          {(home.features.primaryHeating || home.features.hotWater || home.features.ventilationType) && (
            <SectionCard title="Heating & Systems">
              {home.features.primaryHeating && (
                <SpecRow label="Heating" value={home.features.primaryHeating} />
              )}
              {home.features.hotWater && (
                <SpecRow label="Hot water" value={home.features.hotWater} />
              )}
              {home.features.ventilationType && (
                <SpecRow label="Ventilation" value={home.features.ventilationType} />
              )}
            </SectionCard>
          )}
        </VStack>
      </SimpleGrid>
      {process.env.NODE_ENV === 'development' && (
        <>
          <hr />
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            p={4}
            bg="gray.50"
            overflowX="auto"
          >
            <Heading size="sm" mb={3}>
              Home Features Data
            </Heading>
            <pre
              style={{
                margin: 0,
                fontSize: "12px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
              }}
            >
              {JSON.stringify(home.features, null, 2)}
            </pre>
          </Box>
        </>
      )}
    </VStack>
  )
}

export default OverviewTab
